<template>
  <jtn-ui-record-image
    v-observe-visibility="imageVisibility.getOptions('half')"
    :class="[$style.recordImage, [isPost ? $style.post : '']]"
    :author="data.author"
    :caption="data.description"
    :authorLabel="authorLabel"
    :is-post="isPost"
    :display-caption="displayCaption"
  >
    <div
      slot="image"
      ref="vox"
      :class="[$style.inner]"
      :data-hyb-ssp-in-image-overlay="idAdvVOX"
    >
      <jtn-ui-picture :class="$style.pictureWrap" :sources="imageSources">
        <img
          :class="[$style.image, [isInfographic ? $style.infographic : '']]"
          :src="imageUrl"
          :alt="entityDecode(data.description)"
          :title="entityDecode(data.description)"
          :data-vox="voxImgSrc"
          itemprop="contentUrl"
          @click="onImageClicked()"
        />
        <AdvCreative
          v-if="isShowInImageCreative"
          :adv="inImageAdvBlock"
          :clientRender="true"
          :index="index"
          :class="[$style.inImageButton]"
        />
      </jtn-ui-picture>
    </div>

    <jtn-ui-share
      slot="share"
      v-if="!isPost"
      :btn-size="36"
      :is-popup="true"
      :socials="['vk', 'twitter', 'ok', 'telegram', 'wa']"
      :copy-link="shareLink"
      :popup-position="'bottom-left'"
      @shared="shareImage($event)"
      @linkCopied="shareImage('link')"
    />
  </jtn-ui-record-image>
</template>

<script>
import {
  TABLET_MIN_WIDTH,
  LAPTOP_MIN_WIDTH,
  DESKTOP_MIN_WIDTH
} from '@jtnews/shared/data';
import { StoreMixin } from '@jtnews/shared';
import { smoothScroll } from '@jtnews/shared/smooth-scroll';

import { JtnUiRecordImage } from '@jtn-ui/components/record';
import { JtnUiShare, JtnUiPicture } from '@jtnews/jtn-ui';
import { AdvCreative } from '../../../../shared/components/_adv';

import {
  generateImageUrl,
  ObserverVisibility,
  EntityDecoderMixin,
  getImageSources
} from '@jtnews/shared';

export default {
  name: 'RecordImage',
  components: { JtnUiRecordImage, JtnUiShare, JtnUiPicture, AdvCreative },
  mixins: [StoreMixin, EntityDecoderMixin],
  props: {
    data: {
      type: Object,
      default() {
        return {};
      }
    },
    advBlock: {
      type: Object,
      default() {
        return null;
      }
    },
    idAdvVOX: {
      type: String,
      default: ''
    },
    mediaClass: {
      type: String,
      default: 'center'
    },
    authorLabel: {
      string: String,
      default: 'Фото'
    },
    isPost: {
      type: Boolean,
      default: false
    },
    displayCaption: {
      type: Boolean,
      default: true
    },
    shareLink: {
      type: String,
      default: ''
    },
    allowInImageAdv: {
      type: Boolean,
      default: false
    },
    index: {
      type: Number,
      default: undefined
    }
  },
  data() {
    return {
      imageVisibility: new ObserverVisibility('half'),
      isEmptyVoxAdv: false
    };
  },
  computed: {
    imageUrl() {
      return this.getImageUrl(this.data.width);
    },
    imageSources() {
      return this.imageUrl !== ''
        ? [
            ...getImageSources(
              this.getImageUrl(599),
              `(max-width: ${TABLET_MIN_WIDTH - 1}px)`
            ),
            ...getImageSources(
              this.getImageUrl(844),
              `(min-width: ${TABLET_MIN_WIDTH}px, max-width: ${LAPTOP_MIN_WIDTH - 1}px)`
            ),
            ...getImageSources(
              this.getImageUrl(960),
              `(min-width: ${LAPTOP_MIN_WIDTH}px, max-width: ${DESKTOP_MIN_WIDTH - 1}px)`
            ),
            ...getImageSources(
              this.getImageUrl(727),
              `(min-width: ${DESKTOP_MIN_WIDTH}px)`
            )
          ]
        : [];
    },
    voxImgSrc() {
      const isVertical = this.data.width < this.data.height;
      return generateImageUrl(
        this.data,
        false,
        isVertical ? 480 : 640,
        isVertical ? 640 : 480
      );
    },
    isInfographic() {
      return this.mediaClass === 'center-vertical';
    },
    inImageAdvBlock() {
      return this.store.advModule.inImageAdvBlock;
    },
    isShowInImageCreative() {
      return this.inImageAdvBlock && this.allowInImageAdv && this.isEmptyVoxAdv;
    }
  },
  beforeMount() {
    this.voxObserver = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        if (mutation.attributeName === 'data-hyb-ssp-ad-place-status') {
          const attrValue =
            this.$refs.vox.attributes['data-hyb-ssp-ad-place-status'].value;
          if (attrValue === 'empty') {
            this.isEmptyVoxAdv = true;
          }
        }
      });
    });
  },
  mounted() {
    this.$nextTick(() => {
      const imageId = this.$route.query.shareRecordImage;
      if (imageId === this.data.id) {
        this.scrollToImage();
      }
    });

    this.imageVisibility.event.subscribe(state => {
      if (state.value) {
        this.$emit('onImageVisible');
      }
    });

    this.voxObserver.observe(this.$refs.vox, { attributes: true });
  },
  beforeDestroy() {
    this.imageVisibility.destroy();
  },
  methods: {
    getImageUrl(width) {
      return generateImageUrl(this.data, false, Math.min(width, this.data.width));
    },
    onImageClicked() {
      if (!this.isInfographic) {
        this.$emit('imageClicked', this.data.id);
      }
    },
    scrollToImage() {
      setTimeout(() => {
        const clientRect = this.$el.getBoundingClientRect();
        const top = clientRect.top + window.pageYOffset;

        smoothScroll(top, 300);
      }, 700);
    },
    shareImage($event) {
      this.$emit('shareClicked', $event);
    }
  }
};
</script>

<style lang="scss" module>
@import 'styles';

.recordImage {
  display: block;
  clear: both;

  margin: $record-between-indent $central-column-mobile-padding-negative;

  &.post {
    margin: $text-mobile-padding 0;
  }

  @include on-min-resize($tablet) {
    margin: $record-between-indent 0;
  }
}

.inner {
  position: relative;
}

.image {
  max-width: 100%;
  height: auto;

  cursor: pointer;

  &.infographic {
    max-height: none;

    cursor: default;
  }
}

.pictureWrap {
  display: block;

  div[class^='placeholder-'] {
    z-index: 1;
  }
}

.inImageButton {
  position: absolute;
  right: 0;
  bottom: 0;

  width: auto;
  overflow: visible;

  & > div {
    min-width: auto;
  }
}
</style>
